// --- Constants and Mappings for Content ID partial components and pages ---
// General
export const STATUS_COLOR_MAP = {
  processing: {
    text: '#22684B',
    background: '#EDFDF5',
  },
  completed: {
    text: '#3A5985',
    background: '#EFF8FF',
  },
  pending: {
    text: '#7364A7',
    background: '#FAF6FF',
  },
  // Manual-claims itemSummary keys (customer request detail)
  // The API returns `monetized` / `rejected`, but the old badge map used
  // `completed` / `failed`, so `monetized`/`rejected` were falling back.
  monetized: {
    text: '#3A5985',
    background: '#EFF8FF',
  },
  takendown: {
    text: '#22684B',
    background: '#EDFDF5',
  },
  rejected: {
    text: '#E53E3E',
    background: '#FFF5EE',
  },
  failed: {
    text: '#E53E3E',
    background: '#FFF5EE',
  },
};

// Allowlist
export const ALLOWLIST_HEADER_MAP = [
  { display: 'Channel ID', uid: 'id', size: '40%' },
  { display: 'Channel Title', uid: 'title', size: '40%' },
  { display: '', uid: 'actions', size: '20%' },
];

// Assets
// Asset details
export const MATCH_POLICY_ACTIONS = [
  {
    api: 'track',
    display: 'Track',
  },
  {
    api: 'monetize',
    display: 'Monetize',
  },
  {
    api: 'block',
    display: 'Block',
  },
  {
    api: 'routeForReviewToMonetize',
    display: 'Route for Review to Monetize',
  },
];

// Assets list table
export const ASSET_LIST_HEADER_MAP = [
  { display: 'Title', uid: 'title', size: '25%' },
  { display: 'Custom ID', uid: 'customId', size: '15%' },
  { display: 'Type', uid: 'type', size: '10%' },
  { display: 'Claims', uid: 'totalClaims', size: '10%' },
  { display: 'Created', uid: 'timeCreated', size: '25%' },
  { display: '', uid: 'actions', size: '15%' },
];

export const ASSET_TYPE_OPTIONS = [
  { api: 'audio', display: 'Audio' },
  { api: 'video', display: 'Video' },
  { api: 'composition', display: 'Composition' },
  { api: 'art_track_video', display: 'Art Track Video' },
  { api: 'movie', display: 'Movie' },
  { api: 'episode', display: 'Episode' },
  { api: 'web', display: 'Web' },
];

export const ASSET_SORT_OPTIONS = [
  { api: 'total_claims', display: 'Claims' },
  { api: 'approx_daily_engaged_views', display: 'Daily Engaged Views' },
  { api: 'created_at', display: 'Created' },
  { api: 'title', display: 'Title' },
];

export const ASSET_DATE_FILTER_OPTIONS = [
  { api: 'createdAfter', display: 'Created after' },
  { api: 'createdBefore', display: 'Created before' },
];

// Asset uploads
export const MAX_ASSETS_PER_UPLOAD = Number(process.env.MAX_ASSETS_PER_UPLOAD) || 100;

export const ASSET_UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

// Hours after which a non-completed upload request shows a delay warning
export const ASSET_UPLOAD_DELAY_WARNING_HOURS = 3;

export const ASSET_UPLOADS_HEADER_MAP = [
  { display: 'Asset Label', uid: 'assetLabel', size: '20%' },
  { display: 'Status', uid: 'status', size: '15%' },
  { display: 'Created At', uid: 'createdAt', size: '25%' },
  { display: 'Completed At', uid: 'completedAt', size: '25%' },
  { display: '', uid: 'actions', size: '15%' },
];

export const ASSET_UPLOAD_DETAILS_HEADER_MAP = [
  { display: 'File Name', uid: 'fileName', size: '20%' },
  { display: 'Status', uid: 'status', size: '12%' },
  { display: 'YouTube Asset ID', uid: 'youtubeAssetId', size: '15%' },
  { display: 'Started At', uid: 'startedAt', size: '15%' },
  { display: 'Completed At', uid: 'completedAt', size: '15%' },
  { display: 'Error', uid: 'errorMessage', size: '23%' },
];

/** Manual claim request detail — line items table (customer view). */
export const MANUAL_CLAIM_REQUEST_DETAIL_LINE_ITEMS_HEADER_MAP = [
  { display: 'Status', uid: 'status', size: '10%' },
  { display: 'Asset ID', uid: 'asset_id', size: '12%' },
  { display: 'Infringing URL', uid: 'infringing_url', size: '18%' },
  { display: 'Original link', uid: 'original_link', size: '18%' },
  { display: 'Channel Link', uid: 'channel_link', size: '18%' },
  { display: 'Claim type', uid: 'claim_action_type', size: '8%' },
  { display: 'Match / time / channel', uid: 'match_meta', size: '14%' },
  { display: 'Note', uid: 'rejection_note', size: '10%' },
  { display: 'YouTube claim ID', uid: 'youtube_claim_id', size: '10%' },
];

export const ASSET_CSV_HEADERS = [
  {
    key: 'Asset Title',
    description: 'The title of the video asset, e.g. My Video (max. 255 characters)',
    required: false,
  },
  {
    key: 'Asset Description',
    description: 'A brief description of the video asset (max. 5000 characters)',
    required: false,
  },
  {
    key: 'Notes (if any)',
    description: 'Any notes you want to leave for this asset (max. 5000 characters)',
    required: false,
  },
  {
    key: 'Asset Type (Select Web if nothing else applies)',
    description: 'The content type that describes this asset (options are Web, Music Video, Sound Recording, Art Track, Movie, Episode, or Composition)',
    required: false,
  },
  {
    key: 'File Name',
    description: 'The name of the video file being uploaded with extension, e.g. video1.mp4 (please ensure this column matches the uploaded file name exactly)',
    required: true,
  },
  {
    key: 'Ownership',
    description: 'Specify a list of countries where you own (or do not own) the asset. YouTube uses the ownership data that you provide to determine where to apply the policy that you set for that asset',
    required: false,
    special: 'ownership',
  },
  {
    key: 'Reference Exclusions',
    description: 'Reference exclusion intervals allow you to specify one or more time intervals in a reference that YouTube should exclude when determining Content ID matches',
    required: false,
    special: 'referenceExclusions',
  },
  {
    key: 'Match Policy',
    description: 'Specify how you want YouTube to handle this asset (options are monetize, track, block, or routeforreviewtomonetize)',
    required: false,
  },
];

// Claims
// Claims list table
export const CLAIM_HEADER_MAP = [
  { display: '', uid: 'checkbox', size: '3%' },
  { display: 'Claim ID', uid: 'id', size: '11%' },
  { display: 'Asset ID', uid: 'assetId', size: '13%' },
  { display: 'Video Details', uid: 'videoTitle', size: '19%' },
  { display: 'Status', uid: 'status', size: '10%' },
  { display: 'Content Type', uid: 'contentType', size: '11%' },
  { display: 'Engaged Views', uid: 'engagedViews', size: '8%' },
  { display: 'Created', uid: 'timeCreated', size: '12%' },
  { display: '', uid: 'actions', size: '10%' },
];

const CLAIM_STATUS_VALUES = [
  'ACTIVE',
  'INACTIVE',
  'POTENTIAL',
  'TAKEDOWN_REQUESTED',
  'TAKEDOWN_UNDER_REVIEW',
  'TAKENDOWN',
  'APPEALED',
  'DISPUTED',
  'MONETIZATION_REQUESTED',
  'MONETIZATION_UNDER_REVIEW',
  'MONETIZED',
  'REVERT_REQUESTED',
  'RELEASE_REQUESTED',
  'RELEASED',
  'REJECTED',
];

export const SELECTABLE_MONETIZE_STATUSES = ['POTENTIAL'];
export const SELECTABLE_REVERT_STATUSES = ['MONETIZATION_REQUESTED', 'TAKEDOWN_REQUESTED'];
export const SELECTABLE_RELEASE_STATUSES = ['ACTIVE'];
export const SELECTABLE_RELEASE_REVERT_STATUSES = ['RELEASE_REQUESTED'];

export const CLAIM_STATUS_OPTIONS = CLAIM_STATUS_VALUES.map((api) => ({
  api,
  display: api === 'TAKENDOWN' ? 'Taken down' : api.charAt(0) + api.slice(1).toLowerCase().replace(/_/g, ' '),
}));

export const CLAIM_SORT_OPTIONS = [
  { api: 'claim_created_date', display: 'Date' },
  { api: 'engaged_views', display: 'Engaged Views' },
  { api: 'video_title', display: 'Video Title' },
  { api: 'claim_status', display: 'Claim Status' },
];

export const CLAIM_DATE_FILTER_OPTIONS = [
  { api: 'createdAfter', display: 'Created after' },
  { api: 'createdBefore', display: 'Created before' },
];

// Claim history
export const CLAIM_HISTORY_HEADER_MAP = [
  { display: 'Type', uid: 'type', size: '18%' },
  { display: 'Time', uid: 'time', size: '22%' },
  // { display: 'Source', uid: 'source', size: '30%' },
  { display: 'Reason', uid: 'reason', size: '30%' },
];
